import { Link } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar } from "@/components/User/Avatar";
import { Home, Compass, Users, PlusCircle, Settings, LogOut, BookOpen, User } from "lucide-react";
import { ThemeToggle } from "@/components/Layout/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

type MobileMenuProps = {
  open: boolean;
  onClose: () => void;
};

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleSignIn = () => {
    navigate("/auth");
    onClose();
  };

  const handleCreatePrompt = () => {
    if (user) {
      navigate("/create-prompt");
    } else {
      navigate("/auth");
    }
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[85%] sm:w-[350px] p-0">
        <ScrollArea className="h-[calc(100vh-60px)]">
          <div className="px-4 py-6">
            {/* User Profile at the top (logged-in only) */}
            {user && userProfile && (
              <div className="flex items-center gap-3 mb-6">
                <Avatar userId={user.id} avatarId={userProfile.avatarId} size="md" />
                <div>
                  <p className="font-medium">{userProfile.username}</p>
                  <p className="text-xs text-muted-foreground">@{userProfile.username}</p>
                </div>
              </div>
            )}

            {/* Main Navigation */}
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={onClose}>
                <Button variant="ghost" className="justify-start w-full">
                  <Home className="mr-2 h-4 w-4" />
                  Home
                </Button>
              </Link>

              <Link to="/prompts" onClick={onClose}>
                <Button variant="ghost" className="justify-start w-full">
                  <Compass className="mr-2 h-4 w-4" />
                  Explore Prompts
                </Button>
              </Link>

              <Link to="/groups" onClick={onClose}>
                <Button variant="ghost" className="justify-start w-full">
                  <Users className="mr-2 h-4 w-4" />
                  Groups
                </Button>
              </Link>

              <Link to="/blog" onClick={onClose}>
                <Button variant="ghost" className="justify-start w-full">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Blog
                </Button>
              </Link>
            </div>

            {/* Create Prompt Section */}
            <Separator className="my-4" />
            <div className="flex flex-col gap-1">
              <Button variant="ghost" className="justify-start w-full" onClick={handleCreatePrompt}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Create Prompt
              </Button>
            </div>

            {/* User-specific Section */}
            {user ? (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col gap-1">
                  <Link to={`/users/${userProfile?.username}`} onClick={onClose}>
                    <Button variant="ghost" className="justify-start w-full">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Button>
                  </Link>
                  <Link to="/settings" onClick={onClose}>
                    <Button variant="ghost" className="justify-start w-full">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Button>
                  </Link>
                </div>

                <Separator className="my-4" />
                <Button variant="ghost" className="justify-start w-full text-destructive hover:text-destructive" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log Out
                </Button>
              </>
            ) : (
              <>
                <Separator className="my-4" />
                <Button onClick={handleSignIn} className="w-full">
                  Sign In
                </Button>
              </>
            )}

            {/* Theme Toggle */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Theme</span>
              <ThemeToggle />
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
